import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Add,
  CameraAlt,
  ConfirmationNumber,
  Edit,
  QrCode2,
  Settings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppShell from '../components/layout/AppShell';
import DesignSystem from '../designSystem';
import {
  checkPromoterCode,
  deleteMeProfile,
  fetchDeleteMeReasons,
  fetchCurrentSubscription,
  fetchUserBusinesses,
  fetchUserProfile,
  getBusinessLogoUrl,
  ReportReason,
  updateActiveBusiness,
  updateUserProfile,
} from '../services/api';
import { getApiLanguage } from '../config/apiLanguage';
import { useT } from '../i18n/useT';
import { inferSubscriptionSlots } from '../utils/subscription';
import { EmptyState } from '../components/ui';
interface VisiProfile {
  id: string;
  name: string;
  category: string;
  logo?: string;
  isActive: boolean;
  username: string;
}

export default function MyProfilePage() {
  const navigate = useNavigate();
  const { user, logout, refreshUserProfile } = useAuth();
  const t = useT();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [visiProfiles, setVisiProfiles] = useState<VisiProfile[]>([]);
  const [visiLoadError, setVisiLoadError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [promoterCodeDialogOpen, setPromoterCodeDialogOpen] = useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleteReasons, setDeleteReasons] = useState<ReportReason[]>([]);
  const [deleteReasonId, setDeleteReasonId] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const [promoterCode, setPromoterCode] = useState('');
  const [promoterError, setPromoterError] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [allowedActiveVisis, setAllowedActiveVisis] = useState<number | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthday, setBirthday] = useState('');

  useEffect(() => {
    if (!user?.token) return;
    const load = async () => {
      try {
        const { user: profile } = await fetchUserProfile(user.id, user.token);
        setFirstName(profile.firstName || '');
        setLastName(profile.lastName || '');
        if (profile.birthday) {
          const d = profile.birthday.includes('T') ? profile.birthday.split('T')[0] : profile.birthday;
          setBirthday(d);
        } else {
          setBirthday('');
        }
      } catch {
        setFirstName(user.firstName || '');
        setLastName(user.lastName || '');
        setBirthday(user.birthday || '');
      }
    };
    load();
  }, [user]);

  const loadVisis = async () => {
    if (!user?.token) return;
    setVisiLoadError(null);
    try {
      const res = await fetchUserBusinesses(user.token, getApiLanguage());
      const list = res.businesses || [];
      setVisiProfiles(
        list.map((b) => ({
          id: String(b.id),
          name: b.name || b.username || String(b.id),
          category: b.subcategory?.name || b.category?.name || '',
          logo: b.logo ? getBusinessLogoUrl(b.logo) || undefined : undefined,
          isActive: (b.active ?? 0) === 1,
          username: (b.username || '').replace('@', '') || String(b.id),
        }))
      );
    } catch (e) {
      console.error(e);
      setVisiLoadError(t('myProfileLoadVisisFailed'));
      setVisiProfiles([]);
    }
  };

  useEffect(() => {
    void loadVisis();
    if (!user?.token) {
      setAllowedActiveVisis(null);
      return;
    }
    fetchCurrentSubscription(user.token)
      .then((data) => {
        const subscription = data.subscription as Record<string, unknown> | null;
        const type = subscription?.type as Record<string, unknown> | undefined;
        setAllowedActiveVisis(inferSubscriptionSlots(type ?? null));
      })
      .catch(() => setAllowedActiveVisis(null));
    // loadVisis closes over current auth; re-run when identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token, user?.id]);

  const applySavedProfile = (profile: { firstName?: string; lastName?: string; birthday?: string }) => {
    setFirstName(profile.firstName ?? '');
    setLastName(profile.lastName ?? '');
    if (profile.birthday) {
      const d = profile.birthday.includes('T') ? profile.birthday.split('T')[0] : profile.birthday;
      setBirthday(d.includes('.') ? d.split('.').reverse().join('-') : d);
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.token) return;
    const trimmedFirst = firstName.trim();
    if (trimmedFirst.length < 2) {
      setProfileError(t('myProfileFirstNameMin'));
      return;
    }
    setIsLoading(true);
    setProfileError(null);
    try {
      const result = await updateUserProfile(
        user.id,
        {
          firstName: trimmedFirst,
          lastName: lastName.trim(),
          birthday: birthday.trim() || undefined,
          profilePicture: profilePhoto || undefined,
        },
        user.token,
        getApiLanguage()
      );
      if (result.user) {
        applySavedProfile(result.user);
      } else {
        applySavedProfile({ firstName: trimmedFirst, lastName: lastName.trim(), birthday: birthday.trim() || undefined });
      }
      setProfilePhoto(null);
      setEditDialogOpen(false);
      await refreshUserProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      setProfileError(error instanceof Error ? error.message : t('myProfileSaveFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddWithPromoterCode = async () => {
    if (!promoterCode.trim() || !user?.token) return;
    setPromoterError(null);
    setIsLoading(true);
    try {
      const res = await checkPromoterCode(promoterCode.trim(), user.token, getApiLanguage());
      if (res.result === 1 || res.business) {
        setPromoterCodeDialogOpen(false);
        navigate('/visi/create', { state: { promoterCode: promoterCode.trim() } });
      } else {
        setPromoterError(res.message || t('myProfilePromoterInvalid'));
      }
    } catch {
      setPromoterError(t('myProfilePromoterVerifyFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteAccount = async () => {
    if (!user?.token) return;
    setDeleteStep(0);
    setDeleteMessage('');
    setDeleteErr(null);
    setDeleteAccountDialogOpen(true);
    try {
      const reasons = await fetchDeleteMeReasons(user.token, getApiLanguage());
      setDeleteReasons(reasons);
      if (reasons[0]) setDeleteReasonId(String(reasons[0].id));
    } catch (e) {
      setDeleteErr(e instanceof Error ? e.message : t('deleteAccountFailed'));
    }
  };

  const closeDeleteAccount = () => {
    if (isLoading) return;
    setDeleteAccountDialogOpen(false);
    setDeleteStep(0);
    setDeleteErr(null);
  };

  const handleDeleteAccount = async () => {
    if (!user?.token) return;
    if (!deleteReasonId) {
      setDeleteErr(t('deleteAccountNeedReason'));
      setDeleteStep(1);
      return;
    }
    setIsLoading(true);
    setDeleteErr(null);
    try {
      await deleteMeProfile(deleteReasonId, deleteMessage, user.token, getApiLanguage());
      await logout();
      navigate('/?login=1');
    } catch {
      setDeleteErr(t('deleteAccountFailed'));
      setProfileError(t('deleteAccountFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleVisible = async (profile: VisiProfile) => {
    if (!user?.token) return;
    const next = !profile.isActive;
    const activeCount = visiProfiles.filter((p) => p.isActive).length;
    if (next && allowedActiveVisis != null && activeCount >= allowedActiveVisis) {
      setProfileError(
        t('myProfileMaxVisibleReached')
      );
      return;
    }
    setTogglingId(profile.id);
    setProfileError(null);
    try {
      await updateActiveBusiness(Number(profile.id), next, user.token, getApiLanguage());
      setVisiProfiles((prev) =>
        prev.map((p) => (p.id === profile.id ? { ...p, isActive: next } : p))
      );
    } catch (error) {
      setProfileError(error instanceof Error ? error.message : t('myProfileVisibilityFailed'));
    } finally {
      setTogglingId(null);
    }
  };

  const formatBirthday = (dateString: string) => {
    if (!dateString) return '';
    const raw = dateString.trim();
    // Sentinel / placeholder DOBs from the API — do not show
    if (/^0?1[./-]0?1[./-]1900$/.test(raw) || raw.startsWith('1900-01-01')) return '';
    const d = new Date(raw.includes('.') ? raw.split('.').reverse().join('-') : raw);
    if (Number.isNaN(d.getTime())) return raw;
    if (d.getFullYear() <= 1900) return '';
    return d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const displayName = `${firstName || user?.firstName || ''} ${lastName || user?.lastName || ''}`.trim();
  const avatarSrc =
    user?.profilePicture &&
    (user.profilePicture.startsWith('http')
      ? user.profilePicture
      : `${process.env.REACT_APP_API_URL || ''}/assets/img/site/clients/img/${encodeURIComponent(user.profilePicture)}`);

  if (!user) {
    return (
      <AppShell title={t('myProfileTitle')} maxWidth="sm" showCompanion>
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography sx={{ mb: 2, fontWeight: 600 }}>{t('myProfileSignInTitle')}</Typography>
          <Button variant="contained" onClick={() => navigate('/?login=1')} sx={{ textTransform: 'none' }}>
            {t('myProfileLogin')}
          </Button>
        </Box>
      </AppShell>
    );
  }

  const activeCount = visiProfiles.filter((p) => p.isActive).length;
  // Owner profile always lists every owned VISI (incl. agent/test names) so
  // visibility caps / QR / edit stay reachable — same inventory iOS shows.
  const visibleProfiles = visiProfiles;
  const visiCountLabel =
    visiProfiles.length === 0
      ? t('myProfileNoVisisYet')
      : visiProfiles.length === 1
        ? t('myProfileVisiCountOne', { total: visiProfiles.length, visible: activeCount })
        : t('myProfileVisiCountMany', { total: visiProfiles.length, visible: activeCount });

  return (
    <AppShell title={t('myProfileTitle')} maxWidth="lg" hideTitle showCompanion>
      {profileError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProfileError(null)}>
          {profileError}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: DesignSystem.layout.pageGap,
          maxWidth: 720,
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* ME ACCOUNT */}
        <Box
          sx={{
            bgcolor: DesignSystem.colors.surface,
            borderRadius: DesignSystem.borderRadius.lg,
            border: 0,
            boxShadow: 'none',
            p: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: DesignSystem.colors.textSecondary,
              }}
            >
              {t('meAccount')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                aria-label={t('myProfileEditAccountAria')}
                size="small"
                onClick={() => setEditDialogOpen(true)}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: DesignSystem.borderRadius.pill,
                  bgcolor: DesignSystem.colors.surfaceElevated,
                  color: DesignSystem.colors.primary,
                  '&:hover': { bgcolor: DesignSystem.colors.primaryLight },
                }}
              >
                <Edit sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                aria-label={t('myProfileSettingsAria')}
                size="small"
                onClick={() => navigate('/settings')}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: DesignSystem.borderRadius.pill,
                  bgcolor: DesignSystem.colors.surfaceElevated,
                  color: DesignSystem.colors.textSecondary,
                  '&:hover': { bgcolor: DesignSystem.colors.hoverStrong },
                }}
              >
                <Settings sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>

          <Typography sx={{ fontSize: 12.5, color: DesignSystem.colors.textSecondary, lineHeight: 1.4, mb: 2 }}>
            {t('myProfileAccountHint')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Avatar
              src={avatarSrc || undefined}
              sx={{
                width: 76,
                height: 76,
                border: `2px solid ${DesignSystem.colors.primary}`,
                bgcolor: DesignSystem.colors.surfaceElevated,
                flexShrink: 0,
                color: DesignSystem.colors.textTertiary,
              }}
            >
              {!avatarSrc && <CameraAlt sx={{ fontSize: 28 }} />}
            </Avatar>

            <Box sx={{ minWidth: 0, textAlign: 'left' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.03em', color: DesignSystem.colors.textPrimary, mb: 0.25 }}>
                {displayName || user.email?.split('@')[0] || t('myProfileAccountFallback')}
              </Typography>
              {birthday && formatBirthday(birthday) && (
                <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, lineHeight: 1.35 }}>
                  {formatBirthday(birthday)}
                </Typography>
              )}
              <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, lineHeight: 1.35, wordBreak: 'break-all' }}>
                {user.email}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* VISI — main column */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: DesignSystem.colors.textSecondary,
              }}
            >
              {t('visiProfilesSection')}
            </Typography>
            <Typography
              sx={{
                fontSize: 12.5,
                color: activeCount === 0 && visiProfiles.length > 0
                  ? DesignSystem.colors.warning
                  : DesignSystem.colors.textSecondary,
              }}
            >
              {visiCountLabel}
            </Typography>
          </Box>

          {visiProfiles.length > 0 && activeCount === 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 1.75 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={!!togglingId}
                  onClick={() => void toggleVisible(visiProfiles[0])}
                  sx={{ textTransform: 'none', fontWeight: 650, whiteSpace: 'nowrap' }}
                >
                  {t('myProfileMakeFirstVisible')}
                </Button>
              }
            >
              {t('myProfileNoneVisibleWarning')}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, mb: 1.75, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/visi/create')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: DesignSystem.borderRadius.pill,
                px: 2.25,
                py: 0.9,
                boxShadow: 'none',
                backgroundColor: DesignSystem.colors.action,
                '&:hover': { backgroundColor: DesignSystem.colors.actionDark, boxShadow: 'none' },
                '&:active': { transform: 'scale(0.98)' },
              }}
            >
              {t('companionAddNew')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ConfirmationNumber />}
              onClick={() => setPromoterCodeDialogOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: DesignSystem.borderRadius.pill,
                px: 2.25,
                py: 0.9,
                border: 0,
                color: DesignSystem.colors.primary,
                bgcolor: DesignSystem.colors.surface,
                '&:hover': { bgcolor: DesignSystem.colors.surfaceElevated, border: 0 },
                '&:active': { transform: 'scale(0.98)' },
              }}
            >
              {t('myProfileWithPromoterCode')}
            </Button>
          </Box>

          {visiLoadError && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {visiLoadError}
            </Alert>
          )}

          {!visiLoadError && visibleProfiles.length === 0 && (
            <EmptyState
              title={t('myProfileNoPublicVisisTitle')}
              body={t('myProfileNoPublicVisisBody')}
              actionLabel={t('companionAddNew')}
              onAction={() => navigate('/visi/create')}
            />
          )}

          {visibleProfiles.length > 0 && (
            <Box
              sx={{
                bgcolor: DesignSystem.colors.surface,
                borderRadius: DesignSystem.borderRadius.lg,
                border: 0,
                boxShadow: 'none',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 1.75,
                  pt: 1.15,
                  pb: 0.65,
                  borderBottom: `1px solid ${DesignSystem.colors.separator}`,
                }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 650, letterSpacing: '0.05em', textTransform: 'uppercase', color: DesignSystem.colors.textTertiary }}>
                  {t('myProfileVisiColumn')}
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 650, letterSpacing: '0.05em', textTransform: 'uppercase', color: DesignSystem.colors.textTertiary }}>
                  {t('myProfileVisibleColumn')}
                </Typography>
              </Box>

              {visibleProfiles.map((profile, idx) => (
                <Box
                  key={profile.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.15,
                    px: 1.5,
                    py: 1.1,
                    borderBottom: idx < visibleProfiles.length - 1 ? `1px solid ${DesignSystem.colors.separator}` : 0,
                  }}
                >
                  <Box
                    onClick={() => navigate(`/${profile.username}`)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.15, flex: 1, minWidth: 0, cursor: 'pointer' }}
                  >
                    <Avatar
                      src={profile.logo}
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: DesignSystem.colors.primaryLight,
                        color: DesignSystem.colors.primary,
                        fontWeight: 700,
                        fontSize: 15,
                      }}
                    >
                      {profile.name.charAt(0)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.015em', color: DesignSystem.colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.25 }}>
                        {profile.name}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, lineHeight: 1.3 }}>
                        {profile.category || `@${profile.username}`}
                      </Typography>
                    </Box>
                  </Box>

                  <IconButton
                    size="small"
                    aria-label={t('myProfileEditVisiAria')}
                    onClick={() => navigate(`/visi/edit/${profile.id}`)}
                    sx={{ color: DesignSystem.colors.textSecondary, width: 32, height: 32 }}
                  >
                    <Edit sx={{ fontSize: 17 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={t('myProfileQrAria')}
                    onClick={() => navigate(`/visi/qr/${profile.username}?name=${encodeURIComponent(profile.name)}`)}
                    sx={{ color: DesignSystem.colors.primary, width: 32, height: 32 }}
                  >
                    <QrCode2 sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Switch
                    checked={profile.isActive}
                    disabled={togglingId === profile.id}
                    onChange={() => void toggleVisible(profile)}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: DesignSystem.colors.primary },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: DesignSystem.colors.primary },
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) setProfilePhoto(f);
          e.target.value = '';
        }}
      />

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
          {t('myProfileEditAccount')}
          <Button variant="contained" size="small" onClick={() => void handleSaveProfile()} disabled={isLoading} sx={{ textTransform: 'none', boxShadow: 'none' }}>
            {isLoading ? <CircularProgress size={20} /> : t('save')}
          </Button>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar
              sx={{ width: 88, height: 88, mx: 'auto', bgcolor: DesignSystem.colors.surfaceElevated, cursor: 'pointer' }}
              onClick={() => fileInputRef.current?.click()}
            >
              <CameraAlt sx={{ fontSize: 32, color: DesignSystem.colors.textTertiary }} />
            </Avatar>
            <Typography
              sx={{ color: DesignSystem.colors.primary, mt: 1, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
              onClick={() => fileInputRef.current?.click()}
            >
              {profilePhoto ? profilePhoto.name : t('myProfileChangePhoto')}
            </Typography>
          </Box>
          <TextField fullWidth label={t('myProfileFirstName')} value={firstName} onChange={(e) => setFirstName(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label={t('myProfileLastName')} value={lastName} onChange={(e) => setLastName(e.target.value)} sx={{ mb: 2 }} />
          <TextField
            fullWidth
            label={t('myProfileBirthday')}
            type="date"
            value={birthday}
            onChange={(e) => setBirthday(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 3 }}
          />
          <Divider sx={{ mb: 2 }} />
          <Button fullWidth variant="text" onClick={() => void openDeleteAccount()} sx={{ color: DesignSystem.colors.error, textTransform: 'none', justifyContent: 'flex-start' }}>
            {t('deleteAccount')}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={promoterCodeDialogOpen} onClose={() => { setPromoterCodeDialogOpen(false); setPromoterError(null); }} PaperProps={{ sx: { borderRadius: 3, minWidth: 320 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('myProfilePromoterTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontSize: 14, color: DesignSystem.colors.textSecondary }}>
            {t('myProfilePromoterHint')}
          </Typography>
          {promoterError && <Alert severity="error" sx={{ mb: 2 }}>{promoterError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            label={t('myProfilePromoterCode')}
            value={promoterCode}
            onChange={(e) => setPromoterCode(e.target.value.toUpperCase())}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setPromoterCodeDialogOpen(false); setPromoterCode(''); setPromoterError(null); }}>{t('cancel')}</Button>
          <Button onClick={() => void handleAddWithPromoterCode()} variant="contained" disabled={!promoterCode.trim() || isLoading} sx={{ textTransform: 'none', boxShadow: 'none' }}>
            {t('continue')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteAccountDialogOpen} onClose={closeDeleteAccount} PaperProps={{ sx: { borderRadius: 3, minWidth: 320, maxWidth: 440 } }} fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {deleteStep === 0 && t('deleteAccountWarnTitle')}
          {deleteStep === 1 && t('deleteAccountReasonTitle')}
          {deleteStep === 2 && t('deleteAccountConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={deleteStep} alternativeLabel sx={{ mb: 2.5, mt: 0.5 }}>
            <Step><StepLabel /></Step>
            <Step><StepLabel /></Step>
            <Step><StepLabel /></Step>
          </Stepper>
          {deleteErr && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteErr}
            </Alert>
          )}
          {deleteStep === 0 && (
            <Typography sx={{ color: DesignSystem.colors.textSecondary, fontSize: 14, lineHeight: 1.5 }}>
              {t('deleteAccountWarnBody')}
            </Typography>
          )}
          {deleteStep === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography sx={{ color: DesignSystem.colors.textSecondary, fontSize: 14 }}>
                {t('deleteAccountReasonHelp')}
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="delete-reason-label">{t('deleteAccountReasonLabel')}</InputLabel>
                <Select
                  labelId="delete-reason-label"
                  label={t('deleteAccountReasonLabel')}
                  value={deleteReasonId}
                  onChange={(e) => setDeleteReasonId(e.target.value)}
                >
                  {deleteReasons.map((r) => (
                    <MenuItem key={r.id} value={String(r.id)}>
                      {r.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t('deleteAccountMessageLabel')}
                multiline
                minRows={3}
                value={deleteMessage}
                onChange={(e) => setDeleteMessage(e.target.value)}
                fullWidth
              />
            </Box>
          )}
          {deleteStep === 2 && (
            <Typography sx={{ color: DesignSystem.colors.textSecondary, fontSize: 14, lineHeight: 1.5 }}>
              {t('deleteAccountConfirmBody')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={closeDeleteAccount} disabled={isLoading}>
            {t('cancel')}
          </Button>
          {deleteStep > 0 && (
            <Button onClick={() => setDeleteStep((s) => s - 1)} disabled={isLoading}>
              {t('back')}
            </Button>
          )}
          {deleteStep < 2 ? (
            <Button
              variant="contained"
              onClick={() => {
                if (deleteStep === 1 && !deleteReasonId) {
                  setDeleteErr(t('deleteAccountNeedReason'));
                  return;
                }
                setDeleteErr(null);
                setDeleteStep((s) => s + 1);
              }}
              sx={{ textTransform: 'none', boxShadow: 'none' }}
            >
              {t('next')}
            </Button>
          ) : (
            <Button
              onClick={() => void handleDeleteAccount()}
              variant="contained"
              disabled={isLoading}
              color="error"
              sx={{ textTransform: 'none' }}
            >
              {isLoading ? <CircularProgress size={20} sx={{ color: DesignSystem.colors.onPrimary }} /> : t('delete')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </AppShell>
  );
}
